import { useCallback, useEffect, useMemo, useState } from 'react'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { metodoPrograma } from '../api'
import { IoReload } from 'react-icons/io5'
import { MdDelete } from 'react-icons/md'
import 'primereact/resources/themes/lara-light-cyan/theme.css'
import './PrimeTable.css'
import { Search } from 'lucide-react'
import { FiX } from 'react-icons/fi'
import ModalConfirmacao from '../Utils/ModalConfirmacao'
import { useModal } from '../Utils/UseModal'
import { Box, Fade, IconButton, Paper, Popper } from '@mui/material'
import { LuPencil, LuPlus } from 'react-icons/lu'

const initialLazyPrm = (rows) => ({
  page: 0,
  first: 0,
  rows: rows || 10,
  sortField: null,
  sortOrder: null,
})

export default function PrimeTable({
  codigo,
  action,
  head,
  parametros,
  rows,
  onRowSelect,
  columnOptions,
  onDelete,
  refresh,
  title,
  habSearch,
  line,
  collumnsDate = [],
  collumnsDateHour = [],
  collumnsCurrency = [],
  collumnsPercent = [],
  colStyle = [],
  externalPagination = null,
  setExternalPagination,
  dadosTable = null,
  editButton = false,
  fastEditComponent = () => null,
  fastEditStyle = {},
  fastAddStyle = {},
  fastDisablePortal = true,
  addButton = false,
  onAdd = () => null,
  getFastAddInitialValues = () => ({}),
  disablePagination = false,
  fastAddComponent = () => null,
  lineStyle = {},
  actionResponse = null,
  selectionMode,
  selection,
  onSelectionChange,
  rowHighlight,
}) {
  const [loading, setLoading] = useState(true)
  const [dadosProcessados, setDadosProcessados] = useState([])
  const [dsBusca, setDsBusca] = useState(null)
  const [totalRecords, setTotalRecords] = useState(0)
  const [indexaveis, setIndexaveis] = useState([])
  const [lazyParams, setLazyParams] = useState(initialLazyPrm(rows))

  const [anchorEl, setAnchorEl] = useState(null)
  const [anchorElAdd, setAnchorElAdd] = useState(null)
  const [openEdit, setOpenEdit] = useState(false)
  const [openAdd, setOpenAdd] = useState(false)
  const [dadosEditar, setDadosEditar] = useState([])
  const [dadosAdd, setDadosAdd] = useState({})

  const modalConfirmacao = useModal(ModalConfirmacao)
  const fastPopperModifiers = [
    {
      name: 'flip',
      options: { fallbackPlacements: ['top-end', 'bottom-end'] },
    },
    {
      name: 'preventOverflow',
      options: { altAxis: true, padding: 16 },
    },
  ]
  const fastPaperStyle = {
    padding: 5,
    maxWidth: 'calc(100vw - 32px)',
    maxHeight: 'calc(100vh - 32px)',
    overflowY: 'auto',
  }

  const handleClickEdit = (row) => (event) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
    setOpenEdit((prev) => !prev)
    setDadosEditar(row?.original || [])
  }

  const openFastAdd = (event) => {
    setAnchorElAdd(event.currentTarget)
    setDadosAdd(getFastAddInitialValues())
    setOpenAdd(true)
  }

  const handleEdit = useCallback(
    ({ value, index }) => {
      const listaDados = [...dadosEditar]
      listaDados[index] = value
      setDadosEditar(listaDados)
    },
    [dadosEditar],
  )

  const handleEditToAdd = useCallback(({ field, value }) => {
    setDadosAdd((v) => ({ ...v, [field]: value }))
  }, [])

  const componentAdd = useMemo(
    () =>
      fastAddComponent({
        close: () => {
          setOpenAdd(false)
          setDadosAdd({})
        },
        edit: handleEditToAdd,
        values: dadosAdd,
      }),
    [dadosAdd, fastAddComponent, handleEditToAdd],
  )

  const componentEdicao = useMemo(
    () =>
      fastEditComponent({
        linha: dadosEditar,
        close: () => {
          setOpenEdit(false)
          setDadosEditar([])
        },
        edit: handleEdit,
      }),
    [dadosEditar, fastEditComponent, handleEdit],
  )

  useEffect(() => {
    if (dadosTable !== null) {
      setLoading(true)

      const listaTransformada = dadosTable.map((itemOriginal) => ({
        original: itemOriginal,
        formatado: line ? line(itemOriginal) : itemOriginal,
      }))

      setDadosProcessados(listaTransformada)
      if (externalPagination) setTotalRecords(externalPagination?.recordCount)
      else setTotalRecords(dadosTable.length || 0)
      setLoading(false)
    }
  }, [dadosTable, line, externalPagination])

  useEffect(() => {
    if (action && !dadosTable) {
      loadData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lazyParams, refresh, dadosTable])

  const loadData = async (limpa) => {
    setLoading(true)
    if (limpa) {
      setDsBusca(null)
    }

    metodoPrograma(
      action,
      {
        ...parametros,
        dsBusca: !limpa ? dsBusca || null : null,
        CnstPagination: lazyParams,
      },
      codigo,
    ).then((res) => {
      setLoading(false)
      if (typeof actionResponse === 'function') actionResponse(res)
      if (res.status) {
        const listaTransformada = res.lista.map((itemOriginal) => ({
          original: itemOriginal,
          formatado: line ? line(itemOriginal) : itemOriginal,
        }))

        setDadosProcessados(listaTransformada)
        if (res.CnstPagination) {
          setIndexaveis(res.CnstPagination.indexaveis)
          setTotalRecords(res.CnstPagination.recordCount)
        }
      }
    })
  }

  const onPage = (event) => {
    if (externalPagination && setExternalPagination) {
      setExternalPagination(event)
    } else {
      setLazyParams((prev) => ({ ...prev, ...event }))
    }
  }

  const onSort = (event) => {
    const newParams = {
      ...lazyParams,
      sortField: event.sortField,
      sortOrder: event.sortOrder,
    }

    if (externalPagination && setExternalPagination) {
      setExternalPagination(newParams)
    } else {
      setLazyParams(newParams)
    }
  }

  const getCellContent = (valorDaCelula, linha, index) => {
    if (collumnsDate.includes(index)) {
      valorDaCelula = formatDate(valorDaCelula)
    }
    if (collumnsDateHour.includes(index)) {
      valorDaCelula = formatDateHour(valorDaCelula)
    }
    if (collumnsCurrency.includes(index)) {
      valorDaCelula = formatCurrency(valorDaCelula)
    }
    if (columnOptions) {
      const item = columnOptions.find((item) => item.alias === index)
      if (item)
        return item.customJsx({
          index,
          itemFormatado: valorDaCelula,
          linhaFormatada: linha,
          linhaOriginal: linha.original ?? linha,
        })
    }

    return valorDaCelula
  }

  const avisaModal = async (row) => {
    const { original } = row

    const respModal = await modalConfirmacao({
      titulo: `Ao confirmar, este registro será ${
        original?.[onDelete.index] ? 'reativado' : 'removido'
      }. Deseja continuar?`,
    })

    if (respModal) {
      onDelete.action(original)
    }
  }

  const formatDate = (value) => {
    if (!value) return ''
    const d = new Date(value)
    if (isNaN(d)) return value
    return d.toLocaleDateString('pt-BR')
  }

  const formatDateHour = (value) => {
    if (!value) return ''

    const d = new Date(value)
    if (isNaN(d)) return value

    const dia = String(d.getDate()).padStart(2, '0')
    const mes = String(d.getMonth() + 1).padStart(2, '0')
    const ano = d.getFullYear()

    const hora = String(d.getHours()).padStart(2, '0')
    const min = String(d.getMinutes()).padStart(2, '0')
    const seg = String(d.getSeconds()).padStart(2, '0')

    return `${dia}/${mes}/${ano} ${hora}:${min}:${seg}`
  }

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return ''

    const num = Number(value)
    if (isNaN(num)) return value

    const formatted = num.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    })

    return formatted.replace('R$', '').trim()
  }

  return (
    <>
      <DataTable
        value={dadosProcessados}
        lazy={!disablePagination}
        rowClassName={(rowData) =>
          rowHighlight?.(rowData.original) ? 'row-highlight' : ''
        }
        title={title}
        emptyMessage={
          <div
            style={{ textAlign: 'center', padding: '20px', color: '#6c757d' }}
          >
            <i className="pi pi-info-circle" style={{ fontSize: '1.5rem' }}></i>
            <div>Nenhum registro encontrado</div>
          </div>
        }
        resizableColumns
        showGridlines
        paginator={
          !disablePagination &&
          (!!dadosTable || !!setExternalPagination || !!action)
        }
        size="small"
        selectionMode={selectionMode || 'single'}
        selection={selection}
        onSelectionChange={(e) => onSelectionChange && onSelectionChange(e)}
        style={{ padding: 10 }}
        onPage={onPage}
        onSort={onSort}
        rows={externalPagination?.rows || lazyParams.rows}
        first={externalPagination?.first || lazyParams.first}
        totalRecords={totalRecords}
        loading={loading}
        sortField={externalPagination?.sortField || lazyParams.sortField}
        sortOrder={externalPagination?.sortOrder || lazyParams.sortOrder}
        rowsPerPageOptions={[10, 25, 50]}
        onRowSelect={(e) => onRowSelect && onRowSelect(e.data.original)}
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        currentPageReportTemplate="Total de registros {totalRecords}"
        header={
          <>
            {habSearch ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  width: '100%',
                  gap: '6px',
                  alignItems: 'center',
                }}
              >
                <div
                  style={{
                    position: 'relative',
                    width: '40%',
                    maxWidth: '500px',
                  }}
                >
                  <input
                    type="text"
                    placeholder="Buscar..."
                    value={dsBusca || ''}
                    onChange={(e) => setDsBusca(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        loadData()
                      }
                    }}
                    style={{
                      padding: '8px 36px 8px 12px',
                      fontSize: '0.9rem',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontWeight: 300,
                      width: '100%',
                    }}
                  />

                  {dsBusca && (
                    <FiX
                      size={18}
                      onClick={() => {
                        loadData(true)
                      }}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        cursor: 'pointer',
                        color: '#888',
                      }}
                    />
                  )}
                </div>

                <button
                  onClick={() => loadData()}
                  style={{
                    padding: '6px 10px',
                    backgroundColor: '#1976d2',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    height: '38px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Search size={18} />
                </button>
              </div>
            ) : null}
            {addButton && (
              <Box style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <IconButton
                  title="Inserir"
                  onClick={(v) =>
                    componentAdd !== undefined ? openFastAdd(v) : onAdd(v)
                  }
                >
                  <LuPlus size={20} />
                </IconButton>
              </Box>
            )}
          </>
        }
      >
        {selectionMode === 'multiple' && (
          <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
        )}
        {head.map((col, i) => (
          <Column
            key={i}
            field={`${i}`}
            header={col}
            style={colStyle?.[i] || lineStyle}
            headerStyle={{
              fontSize: '13px',
              fontWeight: 600,
            }}
            bodyStyle={{
              fontSize: '13px',
              fontWeight: 400,
            }}
            sortable={indexaveis[i] === true}
            body={(row) => {
              const valorOriginal = row?.formatado?.[i]
              const valor = getCellContent(valorOriginal || '', row, i)

              if (collumnsCurrency.includes(i)) {
                return (
                  <div style={{ display: 'flex', width: '100%' }}>
                    <span
                      style={{
                        marginRight: '4px',
                        color: valorOriginal < 0.0 ? 'red' : 'inherit',
                      }}
                    >
                      R$
                    </span>
                    <span
                      style={{
                        flexGrow: 1,
                        textAlign: 'right',
                        color: valorOriginal < 0.0 ? 'red' : 'inherit',
                      }}
                    >
                      {valor}
                    </span>
                  </div>
                )
              } else if (collumnsPercent.includes(i)) {
                const formatter = new Intl.NumberFormat('pt-BR', {
                  style: 'percent',
                  minimumFractionDigits: 2,
                })

                return (
                  <div style={{ display: 'flex', width: '100%' }}>
                    <span
                      style={{
                        flexGrow: 1,
                        textAlign: 'right',
                        color: valorOriginal < 0.0 ? 'red' : 'inherit',
                      }}
                    >
                      {formatter.format(valor)}
                    </span>
                  </div>
                )
              }

              return valor
            }}
          />
        ))}
        {editButton && componentEdicao && (
          <Column
            columnKey="edicao"
            key="edicao"
            headerStyle={{ width: '60px', textAlign: 'center' }}
            style={{ width: '60px', textAlign: 'center' }}
            body={(row) => (
              <IconButton onClick={handleClickEdit(row)} style={{ padding: 5 }}>
                <LuPencil size={20} />
              </IconButton>
            )}
          />
        )}
        {onDelete && (
          <Column
            key="delete"
            field={`${onDelete.index}`}
            header=""
            headerStyle={{ width: '60px', textAlign: 'center' }}
            style={{ width: '60px', textAlign: 'center' }}
            body={(row) => {
              const deleted = row?.original?.[onDelete.index] || ''

              return deleted ? (
                <IoReload
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    avisaModal(row)
                    e.stopPropagation()
                  }}
                />
              ) : (
                <MdDelete
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    avisaModal(row)
                    e.stopPropagation()
                  }}
                />
              )
            }}
          />
        )}
      </DataTable>
      <Popper
        style={{ zIndex: 99, ...fastAddStyle }}
        open={openAdd}
        anchorEl={anchorElAdd}
        placement="bottom"
        disablePortal={fastDisablePortal}
        modifiers={fastPopperModifiers}
        transition
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <Paper style={fastPaperStyle}>{componentAdd}</Paper>
          </Fade>
        )}
      </Popper>
      <Popper
        style={{ zIndex: 99, ...fastEditStyle }}
        open={openEdit}
        anchorEl={anchorEl}
        placement="top-end"
        disablePortal={fastDisablePortal}
        modifiers={fastPopperModifiers}
        transition
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <Paper style={fastPaperStyle}>{componentEdicao}</Paper>
          </Fade>
        )}
      </Popper>
    </>
  )
}
